import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { PerformanceDatapoint } from "owa-analytics";
import type { CustomData } from "owa-analytics-types";
import type { MailboxInfo } from "owa-client-types";
import { copilotTeachingPopoverStore, getAnchorIdKey, lazyLogBeaconCall, type CopilotTeachingPopoverAnchorID, } from "owa-iris-store";
import { lighted } from "owa-lightning-core-v2";

const defaultLightningId = "CopilotTeachingPopover";

export type CopilotTeachingPopoverBeaconAction = "Click" | "Dismiss";

export interface PreparedCopilotTeachingPopoverBeacon {
    baseCustomData: CustomData;
    end: (additionalCustomData?: CustomData) => void;
}

export async function dismissCopilotTeachingPopover(actionName: string, mailboxInfo?: MailboxInfo, expectedAnchorID?: CopilotTeachingPopoverAnchorID, additionalCustomData?: CustomData, lightningId: string = defaultLightningId): Promise<void> {
    const dismissBeacon = await prepareCopilotTeachingPopoverBeacon({
        actionName,
        beaconAction: "Dismiss",
        mailboxInfo,
        expectedAnchorID,
        additionalCustomData,
        lightningId,
    });
    
    dismissBeacon?.end();
}

/**
 * Prepares a Copilot teaching popover beacon datapoint and base custom data.
 *
 * This is intentionally split into a "prepare" + "end" API so that callers can
 * insert extra logic (e.g. primary action execution) between datapoint creation
 * and addCustomData/end.
 */
export async function prepareCopilotTeachingPopoverBeacon(options: {
    actionName?: string;
    beaconAction: CopilotTeachingPopoverBeaconAction;
    mailboxInfo?: MailboxInfo;
    expectedAnchorID?: CopilotTeachingPopoverAnchorID;
    additionalCustomData?: CustomData;
    lightningId?: string;
}): Promise<PreparedCopilotTeachingPopoverBeacon | undefined> {
    const { actionName, beaconAction, mailboxInfo, expectedAnchorID, additionalCustomData, lightningId = defaultLightningId, } = options;
    
    const store = copilotTeachingPopoverStore();
    
    // Only log when the teaching popover is currently open.
    if (!store.visible || store.anchorID == null) {
        return;
    }
    
    // If an expected anchor was provided, only log when it matches.
    if (expectedAnchorID != null && store.anchorID !== expectedAnchorID) {
        return;
    }
    
    const anchorIDValue = store.anchorID;
    const anchorIDKey = getAnchorIdKey(anchorIDValue);
    
    const trackingBaseUri = store.trackingBaseUri;
    
    const beaconPerfDatapoint = new PerformanceDatapoint("CopilotTeachingBeacon", mailboxInfo ?? getGlobalSettingsAccountMailboxInfo());
    
    const beaconStatus = await lazyLogBeaconCall.importAndExecute(trackingBaseUri, beaconAction);
    
    const campaignPid = new URL(trackingBaseUri).searchParams.get("PID");
    
    const baseCustomData: CustomData = {
        sessionId: store.sessionId,
        prmId: store.irisPrmId,
        anchorID: anchorIDKey,
        campaignPid,
        beaconStatus,
        actionName,
        ...additionalCustomData,
    };
    
    return {
        baseCustomData,
        end: (finalCustomData?: CustomData) => {
            beaconPerfDatapoint.addCustomData({
                ...baseCustomData,
                ...finalCustomData,
            });
            beaconPerfDatapoint.end();
            lighted(lightningId);
        },
    };
}
